<template>
  <multiselect-search
    v-model="optionSelected"
    :open-prefetch="true"
    :allow-empty="allowEmpty"
    :search-url="getSearchUrl"
    :search-options="searchOptions"
    :taggable="taggable"
    :multiple="multiple"
    :multiselectClass="{ 'multiselect-charidy': !defaultStyles, 'my-multiselect': myContatcsPage }"
    skip-external-serching
    :closeOnSelect="true"
    :name="name"
    class="w-100"
    :disabled="disable"
  >
  </multiselect-search>
</template>

<script>
import urls from "@/constants/urls";
import { mapGetters, mapState } from "vuex";
import MultiselectSearch from "@/components/multiselect-search.vue";
import {
  deleteFavoriteCampaign,
  getFavoriteCampaign,
  setFavoriteCampaign,
  getPinnedCampaign,
} from "@/helpers/storage";

const extendParams = [
  "campaign_stats",
  "media",
  "meta",
  "donation_levels",
  "donation_streams",
  "url_alias",
];

export default {
  components: {
    MultiselectSearch,
  },
  props: {
    value: {
      type: [Number, String, Object, Array],
      default: 0,
    },
    getFullCampaign: Boolean,
    defaultStyles: Boolean,
    preselectFirst: Boolean,
    allowEmpty: Boolean,
    taggable: Boolean,
    multiple: Boolean,
    perPage: {
      type: Number,
      default: 100,
    },
    general: Boolean,
    // Opt-in: make the pinned favorite (the star on /campaign) the authoritative
    // default for this selector on every fresh mount. Set only on the campaign-scoped
    // pages (dashboard, event, my-contacts); other selectors keep the legacy default.
    favoriteDefault: Boolean,
    name: {
      type: String,
      default: "",
    },
    myContatcsPage: {
      type: Boolean,
      default: false,
    },
    disable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      innerSelected: null,
      // Whether the mount-time default selection has already been chosen.
      // Reset per component instance, so returning to a page re-applies the default.
      defaultApplied: false,
      // Monotonic guard: a later selection/fetch supersedes an in-flight getCampaignData,
      // so a slow response can't clobber a newer (e.g. user-made) choice.
      fetchSeq: 0,
    };
  },

  mounted() {},

  methods: {
    selectFavorite(options) {
      if (this.allowEmpty) {
        return false;
      }

      // A pinned favorite (the star on /campaign) is the authoritative default,
      // but only where the parent opts in via `favoriteDefault`. Elsewhere the legacy
      // default (last-selected campaign, else the newest) is used.
      const pinned = this.favoriteDefault ? getPinnedCampaign() : null;
      const preferredId = pinned || getFavoriteCampaign() || null;

      const match = preferredId ? options.find((c) => String(c.id) === String(preferredId)) : null;

      if (match) {
        this.optionSelected = this.multiple ? [match] : match;
        return true;
      }

      // Pinned favorite is outside the loaded (paginated) options — fetch it directly.
      // If it can't be resolved (deleted / no access), recover to the newest option.
      if (pinned) {
        this.getCampaignData(pinned).then((resolved) => {
          // Only recover on a genuine failure (null). `undefined` means a newer selection
          // superseded this fetch, in which case we must not clobber it.
          if (resolved === null && options[0]) {
            this.optionSelected = this.multiple ? [options[0]] : options[0];
          }
        });
        return true;
      }

      // No pinned favorite: legacy fallback to the first (newest) option.
      const first = options[0];
      if (first) {
        this.optionSelected = this.multiple ? [first] : first;
        return true;
      }

      return false;
    },

    selectFromQuery() {
      const cidQuery = this.$route.query.cid;
      if (!cidQuery) {
        return false;
      }

      const cid = Number(cidQuery);
      if (isNaN(cid)) {
        return false;
      }

      return cid;
    },

    selectOption() {},

    async getCampaignData(cid) {
      let props = `?${[
        "extend=media",
        "extend=meta",
        "extend=donation_levels",
        "extend=donation_streams",
      ].join("&")}`;

      const seq = ++this.fetchSeq;

      try {
        const campaign = await this.$store.dispatch("getCampaign", {
          cid,
          props,
        });

        if (campaign?.data && seq === this.fetchSeq) {
          this.innerSelected = {
            id: campaign.data.id,
            ...campaign.data.attributes,
          };
          this.optionSelected = this.innerSelected;
          this.$emit("setCampaignData", this.innerSelected);
          return this.innerSelected;
        }
      } catch (e) {
        // Let the caller recover (e.g. fall back to another option), but keep the failure visible.
        console.error("selectCampaign: failed to load campaign", cid, e);
      }

      // A newer selection/fetch superseded this one (on success, no-data, or error alike) —
      // return undefined so the caller doesn't treat it as a failure and clobber the newer choice.
      if (seq !== this.fetchSeq) {
        return undefined;
      }

      // Genuine failure / no data — distinct from the superseded case above.
      return null;
    },
  },

  computed: {
    ...mapState({
      mainCampaigns: (s) => s.campaigns.campaigns,
      toolsCampaigns: (s) => s.tools.campaigns,
    }),
    ...mapGetters({
      getCampaignOptionsDashboard: "getCampaignOptionsDashboard",
    }),

    campaigns() {
      if (this.mainCampaigns.length > 0) {
        return this.mainCampaigns;
      }

      if (this.toolsCampaigns.length > 0) {
        return this.toolsCampaigns;
      }

      return [];
    },

    optionSelected: {
      set(val) {
        // Any new selection supersedes an in-flight getCampaignData fetch.
        this.fetchSeq += 1;
        this.innerSelected = val;
        if (val === null) {
          this.$emit("input", val);
          deleteFavoriteCampaign();
          return;
        }
        setFavoriteCampaign(val.id);
        if (this.getFullCampaign) {
          const cmp = this.campaigns.find((e) => e.id === val.id);
          this.$emit("input", cmp);
        } else {
          if (this.multiple) {
            this.$emit("input", val);
          } else {
            this.$emit("input", val.id);
          }
        }
      },
      get() {
        return this.innerSelected;
      },
    },

    getSearchUrl() {
      const orgID = localStorage.getItem("orgId");

      return urls.getCampaignList.replace(":orgId", orgID);
    },

    searchOptions() {
      return {
        placeholder: "Select campaign",
        changeParams: ({ page }) => ({
          page: page,
          perPage: this.perPage,
          sort_by: ["-startdate", "campaign_mode_dashboard"],
          extend: extendParams,
        }),
        customLabel: (e) => `[${e.id}] ${e.title}`,
        processResults: (data) =>
          data.map((el) => ({
            id: el.id,
            ...el.attributes,
            __origin: el,
          })),
        onListUpdate: (data) => {
          if (data?.length >= this.campaigns?.length) {
            const list = data.map((el) => el.__origin);
            this.$store.commit("setCampaigns", list);
          }
        },
        onIncludedUpdate: (data) => {
          this.$store.commit("setIncludedMedia", data);
        },
      };
    },
  },

  watch: {
    getCampaignOptionsDashboard: {
      handler(list) {
        const fromQuery = this.selectFromQuery();
        if (fromQuery) {
          this.getCampaignData(fromQuery);
          return;
        }

        if (this.general) {
          return;
        }

        // With `favoriteDefault`, the pinned default is applied once per mount so a manual
        // mid-visit pick survives until the page is re-entered. Without it, the legacy
        // behaviour (re-evaluate the default on every list change) is preserved.
        if (this.favoriteDefault && this.defaultApplied) {
          return;
        }

        if (list.length > 0) {
          if (this.preselectFirst) {
            this.optionSelected = this.multiple ? [list[0]] : list[0];
            this.defaultApplied = true;
            return;
          }

          if (this.selectFavorite(list)) {
            this.defaultApplied = true;
          }
        }
      },
      immediate: true,
    },

    value: {
      handler(cid) {
        // On a `favoriteDefault` selector a pinned favorite is authoritative: the parent's
        // v-model value never overrides it (the selector applies the pin via selectFavorite
        // and emits it back to the parent). Not gated on defaultApplied, because on SPA
        // re-entry the campaign list is already loaded and the options watcher may set
        // defaultApplied before this immediate handler runs — which previously let the
        // parent's stale last selection clobber the pin.
        if (this.favoriteDefault && getPinnedCampaign()) {
          return;
        }

        // Compare as strings: campaign ids arrive as strings from the API while some
        // parents coerce the bound value to a Number — a loose compare would refetch forever.
        if (cid && typeof cid === "number" && String(this.innerSelected?.id) !== String(cid)) {
          this.getCampaignData(cid);
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .multiselect-charidy {
  width: 100%;
  min-height: 60px;
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;
  .multiselect__select {
    height: 58px;
    &:before {
      top: 60%;
    }
  }
  .multiselect__option span {
    display: block;
    word-wrap: break-word;
    width: 100%;
    white-space: normal;
  }
  .multiselect__tags {
    min-height: 60px;
  }
  .multiselect__single,
  .multiselect__placeholder {
    font-size: 20px;
    min-height: 40px;
    line-height: 40px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @media only screen and (min-width: 768px) {
      font-size: 26px;
    }
    @media only screen and (min-width: 992px) {
      font-size: 30px;
    }
  }
  .multiselect__input {
    min-height: 40px;
    line-height: 40px;
  }
}
</style>
